import clsx from 'clsx'
import React, { useState } from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import GradingIcon from '@mui/icons-material/Grading'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors'

import { MenuItem, Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId, GridValueFormatterParams } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers'
import { formatDate } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { formatCNPJ, formatCurrency } from 'src/utils/formatter-utils'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import BorderoScope from '../bordero.scopes'
import { Bordero } from '../bordero.types'

const LOG = Logger.get('BORDERO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type BorderoViewProps = {
    className?: string
    scope: BorderoScope
}
class BorderoViewClass implements FCClassContext<BorderoViewProps> {
    // :: Fields
    scope!: BorderoScope
    sytles!: ReturnType<typeof useStyles>
    borderoColumns!: GridColDef[]

    idBorderoInputRef!: React.MutableRefObject<HTMLInputElement | undefined>
    statusInputRef!: React.MutableRefObject<HTMLInputElement | undefined>
    // :: emissors

    readonly emitOnFind = (dataInicial: Dayjs | null, dataFinal: Dayjs | null) => {
        const idBordero = this.idBorderoInputRef.current?.value || undefined
        const status = this.statusInputRef.current?.value || undefined

        this.scope
            .onFind({
                dataInicial: dataInicial?.toDate(),
                dataFinal: dataFinal?.toDate(),
                status: status,
                idBordero: idBordero ? Number(idBordero) : undefined
            })
            .catch(LOG.caught)
    }

    readonly emitOnNew = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitReprocessarClick = (id: GridRowId) => () => {
        this.scope.onReprocess(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(Number(id)).catch(LOG.caught)
    }

    readonly emitShowProcStatusClick = (id: GridRowId) => () => {
        this.scope.onShowBorderoProcStatus(Number(id)).catch(LOG.caught)
    }

    readonly emitShowDetailedResultsClick = (id: GridRowId) => () => {
        this.scope.onShowDetailedResults(Number(id)).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: BorderoViewProps) {
        this.scope = scope
        this.sytles = useStyles()
        this.borderoColumns = static_build_bordero_columns(
            this.emitReprocessarClick,
            this.emitShowDetailedResultsClick,
            this.emitDeleteClick,
            this.emitShowProcStatusClick
        )
    }

    render({ className, scope }: BorderoViewProps) {
        this.scope = bindUpdate(React, scope)
        this.idBorderoInputRef = React.useRef<HTMLInputElement>()
        this.statusInputRef = React.useRef<HTMLInputElement>()

        const [dataInicialSelecionada, setDataInicialSelecionada] = useState<Dayjs | null>(
            dayjs(this.scope.filtroDataInicial)
        )
        const [dataFinalSelecionada, setDataFinalSelecionada] = useState<Dayjs | null>(
            dayjs(this.scope.filtroDataFinal)
        )

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <DatePicker
                        label="Data Inicial"
                        value={dataInicialSelecionada}
                        onChange={(novaData) => setDataInicialSelecionada(novaData)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <DatePicker
                        label="Data Final"
                        value={dataFinalSelecionada}
                        onChange={(novaData) => setDataFinalSelecionada(novaData)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <TextField
                        select
                        inputRef={this.statusInputRef}
                        defaultValue={this.scope.filtroStatus}
                        fullWidth
                        label="Status"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="PENDING">Pendente</MenuItem>
                        <MenuItem value="PROCESSING">Processando</MenuItem>
                        <MenuItem value="PROCESSED">Processado</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        defaultValue={this.scope.filtroIdBordero}
                        className={classes.cnpjInput}
                        inputRef={this.idBorderoInputRef}
                        label="Filtre pelo código do bordero"
                        variant="standard"
                    />

                    <Button
                        variant="outlined"
                        onClick={() => this.emitOnFind(dataInicialSelecionada, dataFinalSelecionada)}
                    >
                        Buscar
                    </Button>
                    <Button variant="outlined" onClick={this.emitOnNew}>
                        Novo
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <DataGrid
                        rows={this.scope.borderos}
                        columns={this.borderoColumns}
                        getRowId={static_getBorderoRowId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />
                </Paper>
            </div>
        )
    }
}

export const BorderoView = classToFComponent(BorderoViewClass, React)
export default BorderoView

function static_build_bordero_columns(
    emitReprocessarClick: (id: GridRowId) => () => void,
    emitShowDetailedResultsClick: (id: GridRowId) => () => void,
    emitDeleteClick: (id: GridRowId) => () => void,
    emitShowProcStatusClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'codigo',
            headerName: 'Código',
            width: 80
        },
        {
            field: 'grupoNome',
            headerName: 'Grupo Proc.',
            width: 150
        },
        {
            field: 'cedenteNome',
            headerName: 'Nome Cedente',
            width: 250
        },
        {
            field: 'cedenteDoc',
            headerName: 'Doc. Cedente',
            width: 140,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return formatCNPJ(params.value)
            }
        },
        {
            field: 'dataEnvio',
            headerName: 'Data Envio',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return !params.value ? '' : formatDate(params.value, 'dd/MM/yyyy HH:mm')
            }
        },
        {
            field: 'dataTerminoProcessamento',
            headerName: 'Fim Proc.',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return !params.value ? '' : formatDate(params.value, 'dd/MM/yyyy HH:mm')
            }
        },
        {
            field: 'faixaRating1',
            headerName: 'Rating 1',
            width: 150
        },
        {
            field: 'valor',
            headerName: 'Valor',
            align: 'right',
            headerAlign: 'right',
            width: 110,
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return formatCurrency(Number(params.value))
            }
        },
        {
            field: 'statusProcessamento',
            headerName: 'Status',
            align: 'center',
            headerAlign: 'center',
            width: 100,
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return params.value === 'PENDENTE'
                    ? 'Pendente'
                    : params.value === 'PROCESSED'
                    ? 'Processado'
                    : 'Processando'
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 140,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                return [
                    <GridActionsCellItem
                        key={'reproc' + id}
                        icon={<RestartAltIcon />}
                        label="Reprocessar"
                        className="textPrimary"
                        onClick={emitReprocessarClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'detailedResult' + id}
                        icon={<GradingIcon />}
                        label="Exibir Resultado Detalhado"
                        disabled={row.statusProcessamento !== 'PROCESSED'}
                        onClick={emitShowDetailedResultsClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'delete' + id}
                        icon={<DeleteIcon />}
                        label="Excluir"
                        onClick={emitDeleteClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'info' + id}
                        icon={<RunningWithErrorsIcon />}
                        label="Status Processamento"
                        onClick={emitShowProcStatusClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getBorderoRowId(row: Bordero) {
    return row.id + ''
}
